<script setup lang="ts">
import { getCategoryRoute, getTranslatedProperty } from "@shopware/helpers";
import { onKeyStroke, useActiveElement } from "@vueuse/core";
import type { Schemas } from "#shopware";
type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const props = defineProps<{
  open?: boolean;
}>();

const { navigationElements } = useNavigation();
const currentMenuPosition = ref<string | undefined>(undefined);
const resetActiveClass = ref<boolean>(true);

const route = useRoute();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const emit = defineEmits<{
  "close-menu": [];
}>();

// Add new ref for tracking hovered items
const hoveredItems = ref<Set<string>>(new Set());

onMounted(() => {
  let currentNaviagtionElement: NavigationElement | undefined;
  if (navigationElements.value) {
    currentNaviagtionElement = findNavigationElement(
      route.path.slice(1),
      navigationElements.value
    );
  }
  if (currentNaviagtionElement) {
    updateActiveClass(
      currentNaviagtionElement.id,
      currentNaviagtionElement.parentId
    );
  }
});

const findNavigationElement = (
  routePath: string,
  navigation: NavigationElement[]
): NavigationElement | undefined => {
  for (let i = 0; i < navigation.length; i++) {
    const navigationElement = navigation[i];
    const seoUrls = navigationElement.seoUrls as
      | Schemas["SeoUrl"][]
      | undefined;
    if (seoUrls) {
      for (let j = 0; j < seoUrls.length; j++) {
        const currentSeoUrl = seoUrls[j];
        if (currentSeoUrl && currentSeoUrl.seoPathInfo === routePath) {
          return navigationElement;
        }
      }
    }
    const children = navigationElement.children;
    if (children) {
      const foundElement = findNavigationElement(routePath, children);
      if (foundElement) {
        return foundElement;
      }
    }
  }
  return undefined;
};

const onUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  updateActiveClass(navigationId, parentId);
};

const resetNavigationActiveClass = (navigation: NavigationElement[]) => {
  for (let ni = 0; ni < navigation.length; ++ni) {
    navigation[ni].activeClass = false;
    const children = navigation[ni].children;
    if (children) {
      resetNavigationActiveClass(children);
    }
  }
};

// Add new methods to handle hover states
const handleMouseEnter = (id: string) => {
  hoveredItems.value.add(id);
  currentMenuPosition.value = id;
};

const handleMouseLeave = (id: string) => {
  hoveredItems.value.delete(id);
  currentMenuPosition.value = undefined;
};

// Modify updateActiveClass to clear hover states
const updateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  const setNavigationActiveClass = (
    navigation: NavigationElement[],
    navigationId: string,
    parentId: string | undefined
  ) => {
    for (let ni = 0; ni < navigation.length; ++ni) {
      if (navigation[ni].id === navigationId) {
        navigation[ni].activeClass = true;
      }
      if (navigation[ni].id === parentId) {
        navigation[ni].activeClass = true;
        if (navigationElements.value) {
          setNavigationActiveClass(
            navigationElements.value,
            navigationId,
            navigation[ni].parentId
          );
        }
      }
      const children = navigation[ni].children;
      if (children) {
        setNavigationActiveClass(children, navigationId, parentId);
      }
    }

    emit("close-menu");
  };

  if (navigationElements.value) {
    hoveredItems.value.clear(); // Clear all hover states on click
    resetNavigationActiveClass(navigationElements.value);
    setNavigationActiveClass(navigationElements.value, navigationId, parentId);
    resetActiveClass.value = false;
  }
};
//#region - keyboard navigation (highliy connected to the HTML structure)
const activeElement = useActiveElement();
onKeyStroke("ArrowDown", (e) => {
  // next parent link or next child link
  if (
    activeElement.value?.parentElement?.nextElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value.parentElement.nextElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

onKeyStroke("ArrowUp", (e) => {
  // previous parent link or previous child link
  if (
    activeElement.value?.parentElement?.previousElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value?.parentElement.previousElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

function isFocusInsideNav() {
  if (activeElement.value) {
    const focusInsideMenu = document
      .getElementsByTagName("nav")[0]
      .contains(activeElement.value);
    return focusInsideMenu;
  }
  return false;
}

onKeyStroke("Escape", () => {
  if (isFocusInsideNav()) {
    currentMenuPosition.value = undefined;
    document
      .getElementsByTagName("header")[0]
      .getElementsByTagName("a")[0]
      .focus();
  }
});
//#endregion - keyboard navigation

// reset when route.path changes
watch(
  () => route.path,
  () => {
    if (resetActiveClass.value === true && navigationElements.value) {
      resetNavigationActiveClass(navigationElements.value);
    }
    resetActiveClass.value = true;
    currentMenuPosition.value = undefined;
  }
);

const expandedIds = ref<string>("");
function isCollapsed(navigationelement: Schemas["Category"]): boolean {
  return expandedIds.value == navigationelement.id;
}

const toggleCollapse = (navigationElement: Schemas["Category"]) => {
  if (isCollapsed(navigationElement)) {
    expandedIds.value = "";
  } else {
    expandedIds.value = navigationElement.id;
  }
};
</script>

<template>
  <ul
    class="lg:flex font-redit_sans_con gap-3 xl:gap-4 max-w-[600px] m-auto lg:max-w-[initial]"
    aria-label="Top navigation"
    role="menu"
  >
    <li
      v-for="(navigationElement, index) in navigationElements"
      :key="navigationElement.id"
      class="menu-item-has-children group"
      @mouseover="handleMouseEnter(navigationElement.id)"
      @mouseleave="handleMouseLeave(navigationElement.id)"
      @focusin="currentMenuPosition = navigationElement.id"
    >
      <span class="nav-item-text"
          v-if="navigationElement.type === 'folder'"
          role="menuitem"
          :class="{
            'font-bold': (navigationElement as NavigationElement).activeClass,
          }"
        >
          {{ getTranslatedProperty(navigationElement, "name") }}
        </span>

      <NuxtLink
        v-else
        role="menuitem"
        :target="
          navigationElement.externalLink || navigationElement.linkNewTab
            ? '_blank'
            : ''
        "
        :to="formatLink(getCategoryRoute(navigationElement))"
        :class="{
          'font-bold': (navigationElement as NavigationElement).activeClass,
        }"
        @click="
          updateActiveClass(navigationElement.id, navigationElement.parentId)
        "
      >
        {{ getTranslatedProperty(navigationElement, "name") }}
      </NuxtLink>

      <client-only>
        <ul
          v-if="navigationElement.children.length > 0"
          class="sub-menu opacity-100 visible pointer-events-none group-hover:pointer-events-auto"
          :class="{
            'block lg:flex':
              expandedIds == navigationElement.id ||
              (navigationElement as NavigationElement).activeClass,
            'lg:visible lg:opacity-100': hoveredItems.has(navigationElement.id),
            'lg:invisible lg:opacity-0': !hoveredItems.has(
              navigationElement.id
            ),
          }"
          @focusin="currentMenuPosition = navigationElement.id"
          @mouseleave="currentMenuPosition = undefined"
        >
          <template v-if="navigationElement.children.length > 0">
            <LayoutTopNavigationRecursive
              :navigation-element-children="navigationElement.children"
              :last-element="
                navigationElements
                  ? navigationElements.length - 1 === index
                  : false
              "
              @update-active-class="onUpdateActiveClass"
              @focusout-last-element="currentMenuPosition = undefined"
              @close-menu="$emit('close-menu')"
            />
          </template>
        </ul>
      </client-only>
      <span
        v-if="navigationElement.children.length > 0"
        class="arrowDown lg:hidden"
        @click="toggleCollapse(navigationElement)"
      >
        <i
          class="fa-solid fa-angle-right"
          :class="{
            'rotate-90':
              expandedIds == navigationElement.id ||
              (navigationElement as NavigationElement).activeClass,
          }"
        >
        </i>
      </span>
    </li>
  </ul>
</template>

<style scoped>
.menu-item-has-children {
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
}

.sub-menu {
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
}
</style>
